import Header from '@/components/layout/Header';
import MaintenanceProjectList from '@/components/maintenance/MaintenanceProjectList';
import ServiceTab from '@/components/maintenance/ServiceTab';
import { useMaintenanceAccess } from '@/hooks/maintenance/useMaintenanceAccess';
import { useMaintenancePermissions } from '@/hooks/maintenance/useMaintenancePermissions';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';
import { validateMaintenanceFilters, type MaintenanceFiltersData } from '@/hooks/maintenance/useMaintenanceFiltersState';

// URL param helpers
const parseArrayParam = (param: string | null): string[] => 
  param ? param.split(',').filter(Boolean) : [];

const arrayToParam = (arr: string[]): string => arr.join(',');

const DEFAULT_FILTERS: MaintenanceFiltersData = {
  searchQuery: '',
  projectGroup: '',
  projectType: [],
  teamLead: '',
  company: '',
  projectMark: '',
  projectRoleUserIds: [],
  projectRoleTypes: [],
  lifecycleStage: '',
  watchSubstatus: '',
  handoverDateFrom: '',
  handoverDateTo: '',
  contractYear: [],
};


const Maintenance = () => {
  const isOptimizedPermsEnabled = useGlobalFeatureFlag('ENABLE_MAINTENANCE_PERMISSIONS');
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use optimized or legacy permissions based on feature flag
  const legacyAccess = useMaintenanceAccess();
  const optimizedPerms = useMaintenancePermissions();
  
  const { hasAccess, isLoading } = isOptimizedPermsEnabled ? optimizedPerms : legacyAccess;
  const navigate = useNavigate();

  // Feature flags - Phase 2: Always enabled
  const ENABLE_SERVICE_TABS = true;

  // Parse initial values from URL (with localStorage fallback)
  const getInitialTab = (): 'mt-projects' | 'all-plan' => {
    const urlTab = searchParams.get('tab');
    if (urlTab === 'mt-projects' || urlTab === 'all-plan') return urlTab;
    try {
      const stored = localStorage.getItem('maintenance-service-active-tab');
      if (stored === 'mt-projects' || stored === 'all-plan') return stored;
    } catch {}
    return 'mt-projects';
  };

  const getInitialFilters = (): MaintenanceFiltersData => {
    // URL params take priority
    const hasUrlParams = searchParams.get('search') || searchParams.get('company') || 
                         searchParams.get('types') || searchParams.get('mark') ||
                         searchParams.get('roleUsers') || searchParams.get('roleTypes') ||
                         searchParams.get('stage') || searchParams.get('from') || searchParams.get('to');
    
    if (hasUrlParams) {
      const stageParam = searchParams.get('stage') || '';
      const validStage = ['yet_transferred','warranty','watch','maintenance'].includes(stageParam) ? stageParam as any : '';
      const ISO = /^\d{4}-\d{2}-\d{2}$/;
      const fromParam = searchParams.get('from') || '';
      const toParam = searchParams.get('to') || '';
      return {
        searchQuery: searchParams.get('search') || '',
        projectGroup: '',
        projectType: parseArrayParam(searchParams.get('types')),
        teamLead: '',
        company: searchParams.get('company') || '',
        projectMark: searchParams.get('mark') || '',
        projectRoleUserIds: parseArrayParam(searchParams.get('roleUsers')),
        projectRoleTypes: parseArrayParam(searchParams.get('roleTypes')),
        lifecycleStage: validStage,
        watchSubstatus: '',
        handoverDateFrom: ISO.test(fromParam) ? fromParam : '',
        handoverDateTo: ISO.test(toParam) ? toParam : '',
        contractYear: [],
      };


    }
    
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem('maintenance_project_filters');
      if (stored) {
        const parsed = JSON.parse(stored);
        const sanitized = validateMaintenanceFilters(parsed);
        // Detect shape mismatch (e.g., projectType was a string) and clear corrupt entry
        const shapeOk =
          parsed && typeof parsed === 'object' &&
          Array.isArray(parsed.projectType) &&
          Array.isArray(parsed.projectRoleUserIds ?? []) &&
          Array.isArray(parsed.projectRoleTypes ?? []);
        if (!shapeOk) {
          console.warn('[Maintenance] Corrupt maintenance_project_filters in localStorage, clearing.', parsed);
          try { localStorage.removeItem('maintenance_project_filters'); } catch {}
        }
        return sanitized;
      }
    } catch {}
    return DEFAULT_FILTERS;
  };

  const getInitialPage = (): number => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    return urlPage > 0 ? urlPage : 1;
  };

  // State lifted to page level
  const [activeTab, setActiveTab] = useState<'mt-projects' | 'all-plan'>(getInitialTab);
  const [filters, setFilters] = useState<MaintenanceFiltersData>(getInitialFilters);
  const [currentPage, setCurrentPage] = useState(getInitialPage);

  // Sync state to URL (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams();
      
      // Tab (omit default)
      if (activeTab !== 'mt-projects') {
        params.set('tab', activeTab);
      }
      
      // Filters (omit empty)
      if (filters.searchQuery) params.set('search', filters.searchQuery);
      if (filters.company) params.set('company', filters.company);
      if (filters.projectType.length > 0) params.set('types', arrayToParam(filters.projectType));
      if (filters.projectMark) params.set('mark', filters.projectMark);
      if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) params.set('roleUsers', arrayToParam(filters.projectRoleUserIds));
      if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) params.set('roleTypes', arrayToParam(filters.projectRoleTypes));
      if (filters.lifecycleStage) params.set('stage', filters.lifecycleStage);
      if (filters.handoverDateFrom) params.set('from', filters.handoverDateFrom);
      if (filters.handoverDateTo) params.set('to', filters.handoverDateTo);
      
      // Pagination (omit default)
      if (currentPage > 1) {
        params.set('page', String(currentPage));
      }
      
      setSearchParams(params, { replace: true });
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [activeTab, filters, currentPage, setSearchParams]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.searchQuery, filters.company, filters.projectType.length, filters.projectMark, 
      filters.projectRoleUserIds?.length, filters.projectRoleTypes?.length,
      filters.lifecycleStage, filters.handoverDateFrom, filters.handoverDateTo]);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('maintenance-service-active-tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('maintenance_project_filters', JSON.stringify(filters));
  }, [filters]);

  // Redirect if user doesn't have access
  useEffect(() => {
    if (!isLoading && !hasAccess) {
      navigate('/projects', { replace: true });
    }
  }, [hasAccess, isLoading, navigate]);

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Đang kiểm tra quyền truy cập...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // No access
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-6">
          <Card>
            <CardContent className="p-6 text-center">
              <h2 className="text-lg font-semibold mb-2">Không có quyền truy cập</h2>
              <p className="text-muted-foreground">
                Bạn không có quyền truy cập trang bảo trì. Vui lòng liên hệ quản trị viên.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto py-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {ENABLE_SERVICE_TABS ? 'Quản lý Dịch vụ' : 'Quản lý Bảo trì'}
          </h1>
          <p className="text-muted-foreground">
            {ENABLE_SERVICE_TABS 
              ? 'Quản lý các dự án đang trong giai đoạn dịch vụ, bảo hành và bảo trì'
              : 'Quản lý các dự án đang trong giai đoạn bảo hành và bảo trì'
            }
          </p>
        </div>
        {ENABLE_SERVICE_TABS ? (
          <ServiceTab 
            activeTab={activeTab}
            onTabChange={setActiveTab}
            filters={filters}
            onFiltersChange={setFilters}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        ) : (
          <MaintenanceProjectList 
            filters={filters}
            onFiltersChange={setFilters}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </main>
    </div>
  );
};

export default Maintenance;